import React from 'react';

import {
  ANTIBODY_DOMAIN_HEIGHT,
  ANTIBODY_DOMAIN_STROKE_THICKNESS,
  ANTIBODY_DOMAIN_WIDTH,
} from 'antibodies/AntibodyConstants';
import {ConnectorSizingParams, LinkerDisplayParams, SharedDomainParams} from 'antibodies/AntibodyTypedefs';

import AntibodyComponentPopover from './AntibodyComponentPopover';

export type AntibodyLinkerProps = LinkerDisplayParams &
  ConnectorSizingParams &
  SharedDomainParams & {
    /* The color of the linker */
    color: string;
  };

/**
 * A long center line with a clockwise arc on each end, intended to link two `AntibodyDomain`.
 * This is an SVG component, so all units are 'abstract user units', relative to the SVG viewbox.
 * @param centerLength The length of the center line
 * @param color The color of the line
 * @param endRadius The radius of the arc
 * @param strokeThickness The thickness of the line
 * @param proteinId The API ID of the protein entity associated with this domain. If passed, the linker will
 *                  be given a SmartChip-style popover with metadata about the protein.
 * @param tipLength The length of the tip after the arc
 */
export default function AntibodyLinker({
  centerLength = ANTIBODY_DOMAIN_HEIGHT,
  color,
  endRadius = ANTIBODY_DOMAIN_WIDTH / 2,
  strokeThickness = ANTIBODY_DOMAIN_STROKE_THICKNESS,
  proteinId,
  inaccessible,
  tipLength = ANTIBODY_DOMAIN_HEIGHT / 8,
}: AntibodyLinkerProps): JSX.Element {
  // Start from the lower end of the link.
  const start = `M 0 ${centerLength + endRadius - tipLength}`;
  // Move to the start of the arc.
  const toBottomArc = `L 0 ${centerLength + endRadius}`;
  // Move along an arc to the bottom of the center.
  const bottom = `A ${endRadius} ${endRadius} 0 0 0 ${2 * endRadius} ${centerLength + endRadius}`;
  // Move up along the center.
  const center = `L ${2 * endRadius} ${endRadius}`;
  // Move along an arc to the top of the upper end.
  const top = `A ${endRadius} ${endRadius} 0 0 1 ${4 * endRadius} ${endRadius}`;
  // Move to the upper end of the link.
  const end = `L ${4 * endRadius} ${endRadius + tipLength}`;
  return (
    <AntibodyComponentPopover proteinId={proteinId} inaccessible={inaccessible}>
      <path
        d={`${start} ${toBottomArc} ${bottom} ${center} ${top} ${end}`}
        stroke={color}
        strokeWidth={strokeThickness}
        fill="none"
      />
    </AntibodyComponentPopover>
  );
}
